// Faceoff countdown bar — small, anchored next to the upcoming
// faceoff dot.
//
// Shows a horizontal "reverse progress bar" during the Faceoff phase
// that counts down toward the puck drop. The bar is fully hidden once
// `remainingTicks < tuning.clock.faceoffHiddenTicks` so the player has
// to feel the last fraction of a second on their own — this is what
// makes the timing component of the faceoff mechanic a skill check.
//
// DOM overlay element: the bar's screen position is computed each tick
// from the current faceoff spot (centre / NZ / end-zone dot) via the
// world→screen projection and applied as a CSS transform, so it always
// sits just below the dot without touching layout.

import {
  FACEOFF_SPOTS,
  type FaceoffSpot,
  type MatchSnapshot,
  faceoffSpotInto,
  tuning,
} from "@arcade-hockey/sim";
import type { PerspectiveCamera } from "three/webgpu";
import { type ScreenSize, simToScreenInto } from "../render/world-mapping.js";

// Bar dimensions in screen pixels. Small so it doesn't dominate the
// viewport but readable — width matches a typical faceoff-circle diameter
// at the default zoom, so the bar sits visually within the circle's
// footprint.
const BAR_WIDTH = 80;
const BAR_HEIGHT = 5;
const BAR_RADIUS = 2.5;
// Vertical offset below the dot, in screen pixels. Tuned so the bar
// sits just below the dot graphic without clipping it.
const BELOW_DOT_PX = 14;

// Scratch buffers — zero-alloc.
const DOT_WORLD = { x: 0, y: 0 };
const DOT_SCREEN = { x: 0, y: 0 };

const BAR_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  left: "0",
  top: "0",
  width: `${BAR_WIDTH}px`,
  height: `${BAR_HEIGHT}px`,
  borderRadius: `${BAR_RADIUS}px`,
  background: "rgba(17, 20, 24, 0.7)",
  display: "none",
  willChange: "transform",
};

const FILL_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  inset: "0",
  borderRadius: `${BAR_RADIUS}px`,
  background: "#ffcc44",
  opacity: "0.95",
  transformOrigin: "left center",
};

export class FaceoffBar {
  readonly root: HTMLDivElement;
  private fill: HTMLDivElement;
  private visible = false;

  constructor() {
    this.root = document.createElement("div");
    Object.assign(this.root.style, BAR_STYLE);
    this.fill = document.createElement("div");
    Object.assign(this.fill.style, FILL_STYLE);
    this.root.appendChild(this.fill);
  }

  update(snap: MatchSnapshot, camera: PerspectiveCamera, screen: ScreenSize): void {
    const inFaceoff = snap.clock.phase === "Faceoff";
    const remaining = snap.clock.remainingTicks;
    const total = tuning.clock.faceoffPauseTicks;
    const hideThreshold = tuning.clock.faceoffHiddenTicks;

    const shouldShow = inFaceoff && remaining > hideThreshold;
    if (shouldShow !== this.visible) {
      this.visible = shouldShow;
      this.root.style.display = shouldShow ? "block" : "none";
    }
    if (!shouldShow) return;

    // Locate the upcoming faceoff dot. Default to Centre when no
    // explicit spot is set (e.g. period start, post-goal celebration).
    const spot: FaceoffSpot = pickSpot(snap.rules.nextFaceoffSpot);
    faceoffSpotInto(DOT_WORLD, spot);
    simToScreenInto(DOT_SCREEN, DOT_WORLD, 0, camera, screen);

    // Centre the bar horizontally on the dot, offset slightly below.
    const x = Math.round(DOT_SCREEN.x - BAR_WIDTH / 2);
    const y = Math.round(DOT_SCREEN.y + BELOW_DOT_PX);
    this.root.style.transform = `translate(${x}px, ${y}px)`;

    const proportion = Math.max(0, Math.min(1, remaining / total));
    this.fill.style.transform = `scaleX(${proportion})`;
  }

  destroy(): void {
    this.root.remove();
  }
}

function pickSpot(declared: FaceoffSpot | undefined): FaceoffSpot {
  if (declared !== undefined) return declared;
  return FACEOFF_SPOTS[0] as FaceoffSpot; // "Center"
}
